import { addAwareCartItem } from './aware-cart-api.js';
// Register before the first add, while the cart drawer is still empty.
import './aware-free-shipping.js';

async function awareAddVariantToCart(variantId, button) {
  if (!variantId || !button || button.disabled) return;
  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding…';
  try {
    const result = await addAwareCartItem(variantId, 'aware-cart-recommendations');
    button.textContent = 'Added';
    if (result.refreshWarning) {
      button.setAttribute('aria-label', 'Item added. Open your cart to check the latest total.');
    }
  } catch (error) {
    console.error('[Aware cart quick add]', error);
    button.textContent = 'Try again';
  } finally {
    button.disabled = false;
    window.setTimeout(() => {
      if (document.body.contains(button)) {
        button.textContent = originalText;
        button.removeAttribute('aria-label');
      }
    }, 1000);
  }
}

/*
 * ==========================================
 * SOMETHING EXTRA
 * ==========================================
 */

document.addEventListener(
  'change',
  (event) => {
    const select =
      event.target.closest(
        '[data-aware-extra-variant]'
      );

    if (!select) return;

    const card =
      select.closest(
        '.aware-cart-extra__item'
      );

    if (!card) return;

    const option =
      select.options[
        select.selectedIndex
      ];

    const button =
      card.querySelector(
        '[data-aware-cart-add]'
      );

    const price =
      card.querySelector(
        '[data-aware-extra-price]'
      );

    if (button) {
      button.dataset.variantId =
        select.value;
    }

    if (
      price &&
      option?.dataset.price
    ) {
      const amount =
        Number(option.dataset.price) / 100;

      const currency =
        window.Shopify?.currency?.active ||
        'USD';

      const locale =
        document.documentElement.lang ||
        'en';

      try {
        price.textContent =
          new Intl.NumberFormat(
            locale,
            {
              style: 'currency',
              currency,
            }
          ).format(amount);
      } catch (_) {
        price.textContent =
          `${currency} ${amount.toFixed(2)}`;
      }
    }
  }
);

document.addEventListener(
  'click',
  async (event) => {
    const button =
      event.target.closest(
        '[data-aware-cart-add]'
      );

    if (!button) return;

    event.preventDefault();

    await awareAddVariantToCart(
      button.dataset.variantId,
      button
    );
  }
);

/*
 * ==========================================
 * COMPLETE THE LOOK
 * ==========================================
 */

class AwareCartRecommendations
  extends HTMLElement {

  constructor() {
    super();

    this.abortController = null;

    this.handleClick =
      this.handleClick.bind(this);

    this.handleChange =
      this.handleChange.bind(this);
  }

  connectedCallback() {
    this.abortController?.abort();

    this.abortController =
      new AbortController();

    this.addEventListener(
      'click',
      this.handleClick,
      {
        signal:
          this.abortController.signal,
      }
    );

    this.addEventListener(
      'change',
      this.handleChange,
      {
        signal:
          this.abortController.signal,
      }
    );

    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get list() {
    return this.querySelector(
      '[data-aware-cart-recommendation-list]'
    );
  }

  get existingProductIds() {
    const raw =
      this.dataset.cartProductIds || '';

    return new Set(
      raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  escapeHTML(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  formatMoney(cents) {
    const amount =
      Number(cents || 0) / 100;

    const currency =
      window.Shopify?.currency?.active ||
      'USD';

    const locale =
      document.documentElement.lang ||
      'en';

    try {
      return new Intl.NumberFormat(
        locale,
        {
          style: 'currency',
          currency,
          maximumFractionDigits: 2,
        }
      ).format(amount);
    } catch (_) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  getImageUrl(product, variant) {
    const variantImage =
      variant?.featured_image;

    if (typeof variantImage === 'string') {
      return variantImage;
    }

    if (variantImage?.src) {
      return variantImage.src;
    }

    if (variantImage?.url) {
      return variantImage.url;
    }

    const productImage =
      product?.featured_image;

    if (typeof productImage === 'string') {
      return productImage;
    }

    if (productImage?.src) {
      return productImage.src;
    }

    if (productImage?.url) {
      return productImage.url;
    }

    if (
      Array.isArray(product?.images) &&
      product.images.length
    ) {
      const firstImage =
        product.images[0];

      if (typeof firstImage === 'string') {
        return firstImage;
      }

      return (
        firstImage?.src ||
        firstImage?.url ||
        ''
      );
    }

    return '';
  }

  async loadRecommendations() {
    const recommendationsUrl =
      this.dataset.recommendationsUrl;

    /*
     * Read every unique product currently
     * present in the cart.
     */

    const sourceProductIds = [
      ...new Set(
        (
          this.dataset.sourceProductIds ||
          ''
        )
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      ),
    ];

    const recommendationLimit =
      Math.max(
        1,
        Number(
          this.dataset.recommendationLimit ||
          10
        )
      );

    const list =
      this.list;

    if (
      !recommendationsUrl ||
      !sourceProductIds.length ||
      !list
    ) {
      this.hidden = true;

      if (list) {
        list.innerHTML = '';
      }

      return;
    }

    try {
      /*
       * Request complementary products for
       * every product in the cart.
       *
       * A failed or empty request for one product
       * does not stop recommendations from the
       * other cart products.
       */

      const recommendationGroups =
        await Promise.all(
          sourceProductIds.map(
            async (productId) => {
              try {
                const url =
                  new URL(
                    recommendationsUrl,
                    window.location.origin
                  );

                url.searchParams.set(
                  'product_id',
                  productId
                );

                url.searchParams.set(
                  'limit',
                  String(recommendationLimit)
                );

                url.searchParams.set(
                  'intent',
                  'complementary'
                );

                const response =
                  await fetch(
                    url.toString(),
                    {
                      headers: {
                        Accept:
                          'application/json',
                      },
                    }
                  );

                if (!response.ok) {
                  throw new Error(
                    'Recommendation request failed for product ' +
                    productId +
                    ': ' +
                    response.status
                  );
                }

                const data =
                  await response.json();

                return data.products || [];
              } catch (error) {
                console.warn(
                  '[Aware cart recommendations]',
                  error
                );

                return [];
              }
            }
          )
        );

      const existingIds =
        this.existingProductIds;

      const seenProductIds =
        new Set();

      /*
       * Merge all recommendation groups.
       *
       * Remove:
       * - unavailable products
       * - products already in the cart
       * - duplicate recommendations
       */

      const products =
        recommendationGroups
          .flat()
          .filter((product) => {
            if (
              !product?.available ||
              product.id == null
            ) {
              return false;
            }

            const productId =
              String(product.id);

            if (
              existingIds.has(productId) ||
              seenProductIds.has(productId)
            ) {
              return false;
            }

            seenProductIds.add(productId);

            return true;
          });

      if (!products.length) {
        this.hidden = true;
        list.innerHTML = '';
        return;
      }

      list.innerHTML =
        products
          .map((product) =>
            this.renderProduct(product)
          )
          .join('');

      this.hidden = false;
    } catch (error) {
      console.warn(
        '[Aware cart recommendations]',
        error
      );

      this.hidden = true;
      list.innerHTML = '';
    }
  }

  renderProduct(product) {
    const availableVariants =
      (product.variants || [])
        .filter(
          (variant) =>
            variant.available
        );

    if (!availableVariants.length) {
      return '';
    }

    const selectedVariant =
      availableVariants[0];

    const imageUrl =
      this.getImageUrl(
        product,
        selectedVariant
      );

    const productUrl =
      product.url ||
      `/products/${product.handle}`;

    const title =
      this.escapeHTML(
        product.title
      );

    const imageMarkup =
      imageUrl
        ? `
          <img
            class="aware-cart-upsell__image"
            src="${this.escapeHTML(imageUrl)}"
            alt="${title}"
            loading="lazy"
            width="180"
            height="180"
          >
        `
        : `
          <div
            class="aware-cart-upsell__image-placeholder"
            aria-hidden="true"
          ></div>
        `;

    let variantMarkup = '';

    if (availableVariants.length > 1) {
      const options =
        availableVariants
          .map((variant) => {
            const label =
              variant.public_title ||
              variant.title ||
              'Option';

            return `
              <option
                value="${variant.id}"
                data-price="${variant.price}"
              >
                ${this.escapeHTML(label)}
              </option>
            `;
          })
          .join('');

      variantMarkup = `
        <select
          class="aware-cart-upsell__variant"
          aria-label="Choose an option for ${title}"
          data-aware-upsell-variant
        >
          ${options}
        </select>
      `;
    }

    return `
      <article
        class="aware-cart-upsell__item"
        data-product-id="${product.id}"
      >

        <a
          class="aware-cart-upsell__image-link"
          href="${this.escapeHTML(productUrl)}"
          aria-label="${title}"
        >
          ${imageMarkup}
        </a>

        <div class="aware-cart-upsell__content">

          <div
            class="aware-cart-upsell__product-line"
          >

            <a
              class="aware-cart-upsell__product-title"
              href="${this.escapeHTML(productUrl)}"
            >
              ${title}
            </a>

            <span
              class="aware-cart-upsell__separator"
              aria-hidden="true"
            >
              -
            </span>

            <span
              class="aware-cart-upsell__price"
              data-aware-upsell-price
            >
              ${this.formatMoney(
                selectedVariant.price
              )}
            </span>

          </div>

          ${variantMarkup}

          <button
            type="button"
            class="aware-cart-upsell__add"
            data-aware-upsell-add
            data-variant-id="${selectedVariant.id}"
          >
            Add to Cart
          </button>

        </div>

      </article>
    `;
  }

  handleChange(event) {
    const select =
      event.target.closest(
        '[data-aware-upsell-variant]'
      );

    if (!select) return;

    const card =
      select.closest(
        '.aware-cart-upsell__item'
      );

    if (!card) return;

    const selectedOption =
      select.options[
        select.selectedIndex
      ];

    const addButton =
      card.querySelector(
        '[data-aware-upsell-add]'
      );

    const price =
      card.querySelector(
        '[data-aware-upsell-price]'
      );

    if (addButton) {
      addButton.dataset.variantId =
        select.value;
    }

    if (
      price &&
      selectedOption?.dataset.price
    ) {
      price.textContent =
        this.formatMoney(
          selectedOption.dataset.price
        );
    }
  }

  async handleClick(event) {
    const button =
      event.target.closest(
        '[data-aware-upsell-add]'
      );

    if (!button) return;

    event.preventDefault();

    await awareAddVariantToCart(
      button.dataset.variantId,
      button
    );
  }
}

if (
  !customElements.get(
    'aware-cart-recommendations'
  )
) {
  customElements.define(
    'aware-cart-recommendations',
    AwareCartRecommendations
  );
}
